<script setup lang="ts">
import { useGlobalCounter } from '@/composables/useGlobalCounter';
import { computed, useAttrs, type Ref } from 'vue';
import DatePicker from 'primevue/datepicker';
import Message from 'primevue/message';
import dayjs, { Dayjs } from 'dayjs';

defineOptions({
    // disable automatic attr inheritance: we do not want to pass attr to the root element,
    // but to the inner field instead
    inheritAttrs: false,
});

export type KpDatePickerProps = {
    label?: string;
    inputId?: string;
    inputClass?: string | object;
    error?: string | null;
};

const value: Ref<any> = defineModel();
withDefaults(defineProps<KpDatePickerProps>(), {
    inputId: () => useGlobalCounter('field'),
    inputClass: '',
    error: '',
});

const emit = defineEmits<{
    (e: 'change', value: any): void;
    (e: 'enter'): void;
}>();

const cls = useAttrs().class || null;
const fieldAttrs = computed(() => {
    const returnObj = { ...useAttrs() };
    // Remove attributes for the inner field we do NOT want to pass along:
    delete returnObj.id;
    delete returnObj.class;
    delete returnObj.label;
    return returnObj;
});

function onKeypress(e: KeyboardEvent) {
    if (e.key.toLowerCase() === 'enter') {
        emit('enter');
    }
}

function onChange(val: any) {
    value.value = dayjs(val);
    emit('change', dayjs(val));
}
</script>

<template>
    <div :class="`field ${cls}`">
        <label v-if="label" :for="inputId" v-html="label"></label>
        <DatePicker
            :modelValue="value && value.isValid() ? value.format() : null"
            showIcon
            fluid
            dateFormat="dd.mm.yy"
            iconDisplay="input"
            inputId="icondisplay"
            v-bind="fieldAttrs"
            @keypress="onKeypress"
            @value-change="onChange"
        >
            <template #inputicon="slotProps">
                <i class="fa-solid fa-calendar" @click="slotProps.clickCallback"></i>
            </template>
        </DatePicker>
        <Message v-if="error" class="error" size="small" severity="error" variant="simple">{{ error }}</Message>
    </div>
</template>

<style lang="css" scoped>
.error {
    --p-message-text-font-weight: normal;
}
</style>
